import {localized} from '@lit/localize';
import type {PropertyValues, TemplateResult} from 'lit';
import {html, LitElement} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import type {ScreenSizeCode} from '../../helpers/elements.ts';
import {overlayHeader} from '../../helpers/elements.ts';
import {Modal} from 'flowbite';
import type {ModalOptions} from '../../stores/store-main.ts';
import {
  adminMessageShown$,
  bottomSheet$,
  modal$,
  stpShortTitles$,
} from '../../stores/store-main.ts';
import {hideModalOnOutsideClick, scrollingElement} from '../../helpers/dom.ts';
import {classMap} from 'lit/directives/class-map.js';
import '../element-contact-form.ts';
import {clearHighlight} from '../../proxies/proxy-highlight.ts';
import {monitorLoadingError} from '../../initialization/obsoleteApi.ts';

@localized()
@customElement('element-overlay')
export default class ElementOverlay extends LitElement {
  @state() options: ModalOptions | undefined;
  @state() private isScrolled: boolean;
  @state() adminMessageShown: boolean = adminMessageShown$.getValue();
  @state() showHeader = true;
  @state() headerMaxScreenSizeCode: ScreenSizeCode | undefined;
  @state() notificationDialog = false;
  @state() titleCloseText = true;
  @state() overlayTitle: TemplateResult<1> | undefined;
  private modal: Modal;
  private galleryOpen = false;
  private pageKey: string;

  showModal() {
    this.modal.show();
    hideModalOnOutsideClick();
  }

  renderContentFactory() {
    switch (this.options.type) {
      case 'contact':
        return html` <element-contact-form
          .overlayContent=${true}
          .feedbackType="${this.options.data?.feedbackType}"
        ></element-contact-form>`;
      case 'staticPage':
        return html` <element-static-overlay-content
          .pageKey=${this.pageKey}
          @close=${() => modal$.next(undefined)}
        >
        </element-static-overlay-content>`;
      case 'confirm':
        return html`<element-dialog-content
          .options="${{
            text: this.options.data.text,
            confirmBtnText: this.options.data.confirmBtnText,
            cancelBtnText: this.options.data.cancelBtnText,
            onConfirm: this.options.data.onConfirm,
            onCancel: this.options.data.onCancel,
          }}"
          @close=${() => {
            if (
              this.options?.type === 'confirm' &&
              this.options?.data?.onClose
            ) {
              this.options.data.onClose();
            }
            modal$.next(undefined);
          }}
        ></element-dialog-content>`;
      case 'importGpx':
        return html`<element-import-gpx-popup
          @close=${() => modal$.next(undefined)}
        >
        </element-import-gpx-popup>`;
      case 'goToOldMapOrLogin':
        return html`<element-account-info-login-enabled
          type="goToOldMapOrLogin"
          @close=${() => modal$.next(undefined)}
        >
        </element-account-info-login-enabled>`;
      case 'sharedTourNotification':
        return html`<element-account-info-login-enabled
          type="sharedTourNotification"
          @close=${() => modal$.next(undefined)}
        >
        </element-account-info-login-enabled>`;
      default:
        return '';
    }
  }

  override render() {
    return html`
      <div
        id="${`element-${this.options.type}-modal`}"
        tabindex="-1"
        class="popup ${classMap({
          message: this.adminMessageShown,
          'popup-content-old-map-or-login-overlay':
            this.options.type === 'goToOldMapOrLogin' ||
            this.options.type === 'sharedTourNotification',
        })} hidden"
      >
        <div
          id="popup-content"
          class="${classMap({
            'popup-content': ![
              'importGpx',
              'goToOldMapOrLogin',
              'sharedTourNotification',
            ].includes(this.options.type)!,
            'popup-content-import-tour': this.options.type === 'importGpx',
            'xl:popup-content-old-map-or-login-xl': [
              'goToOldMapOrLogin',
              'sharedTourNotification',
            ].includes(this.options.type),
            'popup-content-old-map-or-login': [
              'goToOldMapOrLogin',
              'sharedTourNotification',
            ].includes(this.options.type),
            message: this.adminMessageShown,
            'notification-dialog': this.notificationDialog,
          })}"
          @scroll="${(event: Event) =>
            (this.isScrolled = scrollingElement(event))}"
        >
          ${this.showHeader
            ? overlayHeader({
                title: this.overlayTitle,
                scrolled: this.isScrolled,
                closeText: this.titleCloseText,
                onClose:
                  this.options?.type === 'confirm'
                    ? this.options.data?.onClose
                    : undefined,
                maxScreenSizeCode: this.headerMaxScreenSizeCode,
              })
            : ''}
          ${this.renderContentFactory()}
        </div>
      </div>
    `;
  }

  override shouldUpdate() {
    return this.options !== undefined;
  }

  override firstUpdated() {
    this.modal = new Modal(
      this.querySelector(`#element-${this.options.type}-modal`),
      {
        placement: 'center',
        onHide: () => modal$.next(undefined),
        onShow: () => {
          if (this.options?.type === 'sharedTourNotification') {
            this.modal._backdropEl.classList.add('hidden');
          } else {
            this.modal._backdropEl.classList.remove('hidden');
          }
        },
        backdropClasses:
          'modal-backdrop bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-[55]',
      },
    );
    this.showModal();
    adminMessageShown$.subscribe((shown) => (this.adminMessageShown = shown));
  }

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('options'))
      this.querySelector('#popup-content').scrollTo(0, 0);
    super.updated(changedProperties);
  }

  override connectedCallback() {
    modal$.subscribe((options) => {
      if (
        options &&
        !(options.type === 'confirm' && options.data.id === 'delete-filter') &&
        bottomSheet$.value
      ) {
        bottomSheet$.next(undefined);
      }
      // todo add all overlays
      if (options?.type === 'contact') {
        this.options = options;
        if (this.modal) this.showModal();
      } else if (options?.type === 'staticPage') {
        monitorLoadingError(import('./element-static-overlay-content.ts'));
        this.options = options;
        this.pageKey = options?.value;
        this.showHeader = false;
        if (this.modal) this.showModal();
      } else if (options?.type === 'goToOldMapOrLogin') {
        monitorLoadingError(import('./element-account-info-login-enabled.ts'));
        this.options = options;
        this.titleCloseText = false;
        this.headerMaxScreenSizeCode = 'sm';
        if (this.modal) this.showModal();
      } else if (options?.type === 'sharedTourNotification') {
        monitorLoadingError(import('./element-account-info-login-enabled.ts'));
        this.options = options;
        this.titleCloseText = false;
        this.headerMaxScreenSizeCode = 'sm';
        if (this.modal) this.showModal();
      } else if (options?.type === 'confirm') {
        monitorLoadingError(import('./element-dialog-content.ts'));
        this.options = options;
        this.titleCloseText = false;
        this.overlayTitle = html`
          <div class="font-bold">${options.data.title}</div>
        `;
        this.notificationDialog = true;
        if (this.modal) this.showModal();
      } else if (options?.type === 'importGpx') {
        monitorLoadingError(import('./element-import-gpx-popup.ts'));
        this.options = options;
        this.showHeader = false;
        if (this.modal) this.showModal();
      } else if (options?.type === 'gallery') {
        this.galleryOpen = true;
      } else if (this.options && !this.galleryOpen) {
        if (this.modal.isVisible()) {
          this.modal.hide();
        }
        if (this.options?.type === 'staticPage')
          stpShortTitles$.next(undefined);
        this.options = undefined;
        this.titleCloseText = true;
        this.notificationDialog = false;
        clearHighlight(false);
      } else {
        this.galleryOpen = false;
      }
      // set default values
      if (options === undefined) {
        this.showHeader = true;
        this.notificationDialog = false;
        this.titleCloseText = true;
        this.headerMaxScreenSizeCode = undefined;
      }
    });
    super.connectedCallback();
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-overlay': ElementOverlay;
  }
}
